"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { X } from "lucide-react";
import { useViewportKeyboard } from "@/components/editor/hooks/useViewportKeyboard";

interface UseBottomSheetReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  isMobile: boolean;
}

/**
 * 바텀시트 상태 관리 훅
 * 반응형 감지, body scroll lock, viewport 감지
 */
export function useBottomSheet(initialOpen = false): UseBottomSheetReturn {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 639px)");
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMobile((prev) => {
      const current = mediaQuery.matches;
      return prev !== current ? current : prev;
    });

    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  // 열릴 때 body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  return { isOpen, open, close, isMobile };
}

interface BottomSheetWrapperProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  /** 데스크톱 max-width 크기. 기본값 "md" (448px) */
  size?: "sm" | "md" | "lg";
  /** 빌트인 헤더 제목 */
  title?: string;
  /** 빌트인 헤더 아이콘 (ReactNode) */
  icon?: React.ReactNode;
}

const sizeClasses = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
} as const;

/**
 * 고도화된 반응형 바텀시트 컴포넌트
 *
 * 공통: ESC 닫기, 포커스 트랩, 빌트인 헤더
 * 모바일: 전체 너비 + 드래그 핸들 바
 * 데스크톱: 중앙 정렬 + max-width + backdrop-blur + 큰 그림자
 */
export function BottomSheetWrapper({
  isOpen,
  onClose,
  children,
  className,
  size = "md",
  title,
  icon,
}: BottomSheetWrapperProps): React.ReactNode {
  const { keyboardHeight, isKeyboardOpen } = useViewportKeyboard();
  const sheetRef = useRef<HTMLDivElement>(null);

  // ESC 키로 닫기
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // 포커스 트랩
  useEffect(() => {
    if (!isOpen || !sheetRef.current) return;

    const sheet = sheetRef.current;
    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    // 열릴 때 첫 포커스 가능 요소로 이동
    const firstFocusable = sheet.querySelector<HTMLElement>(focusableSelector);
    firstFocusable?.focus();

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const focusables = sheet.querySelectorAll<HTMLElement>(focusableSelector);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <React.Fragment>
      {/* Backdrop: 데스크톱은 backdrop-blur */}
      <div
        className="fixed inset-0 z-[99] bg-black/30 sm:backdrop-blur-[2px]"
        onClick={onClose}
      />
      {/* Bottom sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx(
          "fixed bottom-sheet-spring",
          // 모바일: 전체 너비 (좌우 16px)
          "left-4 right-4",
          // 데스크톱: 중앙 정렬 + max-width
          "sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-full",
          sizeClasses[size],
          // 공통 스타일
          "z-[100] bg-white overflow-hidden",
          // 모바일: 기본 라운드/그림자
          "rounded-xl shadow-lg border border-neutral-200",
          // 데스크톱: 더 큰 라운드/그림자
          "sm:rounded-2xl sm:shadow-2xl sm:border-neutral-300",
          className
        )}
        style={{
          bottom: isKeyboardOpen && keyboardHeight > 0 ? `${keyboardHeight + 16}px` : "16px",
          transition: "bottom 0.15s ease-out",
        }}
      >
        {/* 모바일 드래그 핸들 바 */}
        <div className="flex justify-center pt-2 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-neutral-300" />
        </div>

        {/* 빌트인 헤더 (title이 있을 때만) */}
        {title && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
            <div className="flex items-center gap-2">
              {icon && (
                <span className="text-[var(--color-pad-line-blue)]">{icon}</span>
              )}
              <span className="text-sm font-semibold text-neutral-800">{title}</span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* 컨텐츠 영역 */}
        <div className="max-h-[70vh] overflow-y-auto">
          {children}
        </div>
      </div>
    </React.Fragment>,
    document.body
  );
}
